/* ═══════════════════════════════════════════════════════════════
   Export from Masters — Variant → Modules → Apply a forma? → Export N.
   Read-only road: exports the PUBLISHED master (coreContent) through the
   same engine as the project export, with a stub project. Nothing is
   written to any project, master, or module.
   docs/MODULE_ASSEMBLY_PLAN.md §5
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/SearchableSelect';
import { VariantPickCell } from '../components/VariantPickCell';
import { AssembleModulesDialog, canAssembleModules } from '../components/AssembleModulesDialog';
import { FormaService } from '../services/formaService';
import { prepareSpecsForExport, exportSpecsViaServer } from '../services/specExportPipeline';
import { isArchived } from '../lib/archive';
import { isWeb } from '../lib/platform';
import { forkMasterBlocks, visibleComposedBlocks } from '../../shared/spec-fork';
import { EMPTY_PICKS, type ModulePicks } from '../../shared/spec-assemble';
import type { ProjectSpec, SpecMaster, StandardConfig } from '../../shared/types';

const OFFICE_MASTER = '__office__';

type Step = 'variant' | 'modules' | 'forma';
const STEP_LABEL: Record<Step, string> = {
  variant: 'Variant',
  modules: 'Modules',
  forma: 'Forma',
};

export interface ExportMastersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  masters: SpecMaster[];
  /** Studio has unpublished edits open — the dialog says what is exported. */
  hasDraft?: boolean;
  userPermissions?: string[];
  userName?: string;
  toast: (msg: string, isError?: boolean, opts?: { ms?: number }) => void;
}

export function ExportMastersDialog({
  open,
  onOpenChange,
  masters,
  hasDraft,
  userPermissions,
  userName,
  toast,
}: ExportMastersDialogProps) {
  const hasVariants = masters.some((m) => (m.layers || []).length > 0);
  const canModules = canAssembleModules(userPermissions);
  const steps = useMemo<Step[]>(
    () => [
      ...(hasVariants ? (['variant'] as Step[]) : []),
      ...(canModules ? (['modules'] as Step[]) : []),
      'forma',
    ],
    [hasVariants, canModules]
  );
  const [stepIdx, setStepIdx] = useState(0);
  const step = steps[Math.min(stepIdx, steps.length - 1)];
  const [variantPick, setVariantPick] = useState<Record<string, string>>({});
  const [modulePick, setModulePick] = useState<Record<string, ModulePicks>>({});
  const [formas, setFormas] = useState<StandardConfig[]>([]);
  const [formaId, setFormaId] = useState('');
  const [format, setFormat] = useState<'docx' | 'pdf' | 'both'>('docx');
  const [dir, setDir] = useState('');
  const [exporting, setExporting] = useState(false);
  /** Set by the Modules stepper's Done so its own close is not read as a cancel. */
  const modulesDoneRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setVariantPick({});
    setModulePick({});
    setFormaId('');
    setFormat('docx');
    FormaService.list()
      .then((list) => {
        setFormas(list);
        // No firm-level default exists: preselect the first usable forma by name.
        const first = list
          .filter((f: any) => !f.projectId && !isArchived(f))
          .map((f) => ({ id: f.id, name: f.docSettings?.standardName || 'Untitled' }))
          .sort((a, b) => a.name.localeCompare(b.name))[0];
        setFormaId((cur) => cur || first?.id || '');
      })
      .catch(() => setFormas([]));
  }, [open]);

  const layerFor = (m: SpecMaster) => {
    const pick = variantPick[m.id];
    return pick && pick !== OFFICE_MASTER ? (m.layers || []).find((l) => l.id === pick) : undefined;
  };

  const formaOptions = [
    ...formas
      .filter((f: any) => !f.projectId && !isArchived(f))
      .map((f) => ({
        value: f.id,
        label:
          (f.docSettings?.standardName || 'Untitled') +
          (f.docSettings?.clientName ? ` — ${f.docSettings.clientName}` : ''),
      })),
  ];

  async function runExport() {
    if (!isWeb && !(window as any).api?.exportProjectSpecs) {
      toast(
        'Export API not available — the desktop app needs to restart to pick up the new build.',
        true
      );
      return;
    }
    let outDir = dir;
    if (!isWeb && !outDir) {
      const picked = await (window as any).api?.selectFolderDialog?.();
      if (!picked) {
        toast('Pick an output folder first', true);
        return;
      }
      outDir = picked;
      setDir(picked);
    }
    setExporting(true);
    try {
      const forma = formas.find((f) => f.id === formaId) || null;
      if (!forma) {
        toast('Create a forma first.', true);
        return;
      }
      const formaConfig = forma ? JSON.parse(JSON.stringify(forma)) : null;
      const now = new Date().toISOString();
      const specs: ProjectSpec[] = masters.map((m) => ({
        id: 'exp-' + m.id,
        projectId: '',
        masterId: m.id,
        masterVersion: m.version,
        csiNumber: m.csiNumber,
        title: m.title,
        description: m.description,
        coreContent: forkMasterBlocks(m, layerFor(m), modulePick[m.id] ?? EMPTY_PICKS, forma),
        systems: JSON.parse(JSON.stringify(m.systems || [])),
        variants: [],
        compiledAt: now,
        lastEdited: now,
      }));
      const prepared = await prepareSpecsForExport(specs, {
        project: { id: '', name: '', number: '' },
        allProjectSpecs: [],
      });
      // Project tokens stay literal placeholders (keepUnboundTokens); no unbound-placeholder gate.
      const res = isWeb
        ? await exportSpecsViaServer({
            specs: prepared,
            formaConfig,
            projectMeta: { keepUnboundTokens: 'true' },
          })
        : await (window as any).api.exportProjectSpecs({
            specs: prepared,
            formaConfig,
            outputDir: outDir,
            format,
            projectMeta: { keepUnboundTokens: 'true' },
          });
      const docxOk = (res?.docx || []).filter((r: any) => r.success).length;
      const pdfOk = (res?.pdf || []).filter((r: any) => r.success).length;
      const fails = [
        ...(res?.docx || []).filter((r: any) => !r.success),
        ...(res?.pdf || []).filter((r: any) => !r.success),
      ];
      if (fails.length) {
        console.warn('[export-masters] failures:', fails);
        toast(
          `Export failed for ${fails.length} file${fails.length > 1 ? 's' : ''}: ` +
            (String((fails[0] as any).error || '').trim() || 'Unknown error'),
          true
        );
        return;
      }
      const parts: string[] = [];
      if (format !== 'pdf') parts.push(`${docxOk} docx`);
      if (format !== 'docx') parts.push(`${pdfOk} pdf`);
      toast(
        isWeb ? `Downloaded ${parts.join(' + ')}` : `Exported ${parts.join(' + ')} to ${outDir}`
      );
      if (!isWeb) (window as any).api?.openFolder?.(outDir);
      onOpenChange(false);
    } catch (err: any) {
      toast(`Export failed: ${err?.message || err}`, true);
    } finally {
      setExporting(false);
    }
  }

  const isLast = stepIdx >= steps.length - 1;
  const n = masters.length;

  return (
    <>
      <Dialog open={open && step !== 'modules'} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              Export {n === 1 ? masters[0].csiNumber || masters[0].title : `${n} masters`}
            </DialogTitle>
            <DialogDescription>
              {steps.map((s, i) => (
                <span key={s} className={i === stepIdx ? 'font-semibold text-text' : 'text-subtle'}>
                  {i > 0 ? ' › ' : ''}
                  {i + 1}. {STEP_LABEL[s]}
                </span>
              ))}
              {hasDraft ? <span> · Exports the published version.</span> : null}
            </DialogDescription>
          </DialogHeader>

          {step === 'variant' && (
            <div className="flex flex-col gap-2">
              {masters.map((m) => (
                <div key={m.id} className="flex items-center gap-3">
                  <span className="w-28 shrink-0 font-mono font-semibold text-primary">
                    {m.csiNumber || '—'}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {m.title || 'Untitled'}
                  </span>
                  <div className="w-64 shrink-0">
                    <VariantPickCell
                      layers={m.layers || []}
                      value={variantPick[m.id]}
                      officeMaster={OFFICE_MASTER}
                      label={m.csiNumber || m.title || ''}
                      onChange={(v) => setVariantPick((prev) => ({ ...prev, [m.id]: v }))}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 'forma' && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold uppercase tracking-wide text-subtle">
                  Apply a forma?
                </span>
                <SearchableSelect
                  value={formaId}
                  onChange={(v) => v && setFormaId(v)}
                  options={formaOptions}
                  placeholder="Choose a forma"
                />
                <span className="text-xs text-subtle">
                  {formaOptions.length === 0
                    ? 'Create a forma first.'
                    : 'Formatting template applied to every exported spec. Project fields stay as placeholders.'}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold uppercase tracking-wide text-subtle">
                  Format
                </span>
                <div className="flex gap-1.5">
                  {(['docx', 'pdf', 'both'] as const)
                    .filter((f) => !isWeb || f === 'docx')
                    .map((f) => (
                      <Button
                        key={f}
                        variant={format === f ? 'default' : 'outline'}
                        className="flex-1 uppercase"
                        onClick={() => setFormat(f)}
                      >
                        {f === 'both' ? 'Both' : f}
                      </Button>
                    ))}
                </div>
              </div>
              {isWeb ? (
                <div className="text-sm text-subtle">
                  Files download to your browser&rsquo;s download folder.
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold uppercase tracking-wide text-subtle">
                    Output Folder
                  </span>
                  <div className="flex gap-1.5">
                    <Input value={dir} readOnly placeholder="Choose a folder…" className="flex-1" />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        const d = await (window as any).api?.selectFolderDialog?.();
                        if (d) setDir(d);
                      }}
                    >
                      Browse
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" disabled={exporting} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {stepIdx > 0 && (
              <Button
                variant="outline"
                disabled={exporting}
                onClick={() => setStepIdx((i) => i - 1)}
              >
                Back
              </Button>
            )}
            {isLast ? (
              <Button disabled={exporting || n === 0 || !formaId} onClick={() => void runExport()}>
                {exporting ? 'Exporting…' : `Export ${n}`}
              </Button>
            ) : (
              <Button onClick={() => setStepIdx((i) => i + 1)}>Next</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {open && step === 'modules' && (
        <AssembleModulesDialog
          open
          onOpenChange={(o) => {
            if (o) return;
            // Done also closes the stepper — that is not a cancel.
            if (modulesDoneRef.current) {
              modulesDoneRef.current = false;
              return;
            }
            // Cancel/Escape steps back (or closes when it is the first step).
            if (stepIdx > 0) setStepIdx((i) => i - 1);
            else onOpenChange(false);
          }}
          specs={masters.map((m) => ({
            id: m.id,
            number: m.csiNumber || '',
            title: m.title || 'Untitled',
            blocks: visibleComposedBlocks(m, layerFor(m)),
          }))}
          initialPicks={modulePick}
          userPermissions={userPermissions}
          userName={userName}
          toast={toast}
          onDone={(picks) => {
            modulesDoneRef.current = true;
            setModulePick(picks);
            setStepIdx((i) => i + 1);
          }}
        />
      )}
    </>
  );
}

export default ExportMastersDialog;
